import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import {
  fetchCounties,
  fetchStateFmr,
  lookupFmrByFips,
  lookupFmrByZip,
  type CountyEntry,
  type FmrResult,
  type StateFmrEntry,
} from "./fmr.server";

export type { CountyEntry, FmrResult, FmrRents, StateFmrEntry } from "./fmr.server";

/** Look up Small Area FMR for a 5-digit ZIP (2026 default). */
export const getFmrByZip = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z
      .object({
        zip: z.string().regex(/^\d{5}$/, "Enter a 5-digit ZIP code"),
        year: z.number().int().min(2020).max(2030).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<FmrResult> => {
    const year = data.year ?? 2026;
    return lookupFmrByZip(data.zip, year);
  });

/** Look up FMR by county FIPS (5- or 10-digit) or ZIP — fallback when ZIP has no SAFMR. */
export const getFmrByFips = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z
      .object({
        fips: z
          .string()
          .trim()
          .regex(/^\d{5}(\d{5})?$/, "Enter a 5- or 10-digit county FIPS code")
          // HUD expects 10-digit county codes: 5-digit county + 99999 sub-area
          .transform((v) => (v.length === 5 ? `${v}99999` : v)),
        year: z.number().int().min(2020).max(2030).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<FmrResult> => {
    const year = data.year ?? 2026;
    return lookupFmrByFips(data.fips, year);
  });


/** List all counties in a state (for the county picker). */
export const listCounties = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ state: z.string().length(2) }).parse(input))
  .handler(async ({ data }): Promise<CountyEntry[]> => {
    return fetchCounties(data.state);
  });

/** Statewide FMR — one row per county/metro. Powers the Section 8 investing map. */
export const getStateFmr = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z
      .object({
        state: z.string().length(2),
        year: z.number().int().min(2020).max(2030).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data }): Promise<{ state: string; year: number; entries: StateFmrEntry[] }> => {
    const year = data.year ?? 2026;
    return fetchStateFmr(data.state, year);
  });
